import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { ConsultationForm } from "../components/ConsultationForm";
import {
  CheckIcon,
  ClockIcon,
  MailIcon,
  PinIcon,
} from "../components/icons";
import { usePageMeta } from "../hooks/usePageMeta";
import { features, site } from "../lib/site";

const whatHappensNext = [
  "We read your brief and reply by email, usually the next business day.",
  "A 30-minute call to dig into the problem, constraints, and budget.",
  "A written proposal with scope, team shape, timeline, and a fixed price.",
];

export const ContactPage = () => {
  const [referenceId, setReferenceId] = useState<string | null | undefined>();
  const confirmationRef = useRef<HTMLDivElement>(null);
  usePageMeta(
    "Contact CatalyxLabs — Book a consultation",
    "Tell CatalyxLabs about your project and book a free 30-minute consultation call. We reply within one business day.",
  );

  const isSubmitted = referenceId !== undefined;

  // Swapping the tall form for the shorter confirmation shrinks the document
  // and leaves the panel off-screen. Wait a frame for layout to settle, then
  // scroll explicitly — relying on focus()'s implicit scroll races the reflow.
  useEffect(() => {
    if (!isSubmitted) return;

    const frame = requestAnimationFrame(() => {
      const panel = confirmationRef.current;
      if (!panel) return;
      panel.scrollIntoView({ block: "start", behavior: "auto" });
      panel.focus({ preventScroll: true });
    });

    return () => cancelAnimationFrame(frame);
  }, [isSubmitted]);

  return (
    <>
      <div className="page-header">
        <span className="eyebrow">Contact</span>
        <h1 className="page-header__title">Let&apos;s scope your project</h1>
        <p className="page-header__lede">
          Share what you have — a full spec, a rough idea, or just a problem
          that keeps coming up. We&apos;ll tell you honestly whether we can help
          and what it would take.
        </p>
      </div>

      <div className="contact">
        {isSubmitted ? (
          <div
            className="confirmation"
            ref={confirmationRef}
            tabIndex={-1}
            role="status"
          >
            <span className="confirmation__icon">
              <CheckIcon />
            </span>
            <h2>Thanks — your request is in</h2>
            <p className="confirmation__body">
              We&apos;ve got your brief and someone on the team is reading it.
              Expect a reply at the address you gave us {site.responseTime}.
            </p>
            {referenceId ? (
              <p className="confirmation__ref">Reference: {referenceId}</p>
            ) : null}
            <div className="confirmation__actions">
              <Link
                to={features.portfolio ? "/portfolio" : "/"}
                className="btn btn--secondary"
              >
                {features.portfolio ? "Browse our work" : "Back to home"}
              </Link>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setReferenceId(undefined)}
              >
                Submit another request
              </button>
            </div>
          </div>
        ) : (
          <div className="contact__layout">
            <ConsultationForm onSuccess={setReferenceId} />

            <aside className="contact__aside">
              <div className="contact__panel">
                <h2 className="contact__panel-title">Reach us directly</h2>
                <ul className="contact__list">
                  <li className="contact__list-item">
                    <MailIcon className="contact__list-icon" />
                    <span>
                      <span className="contact__list-label">Email</span>
                      <a
                        className="contact__list-value"
                        href={`mailto:${site.email}`}
                      >
                        {site.email}
                      </a>
                    </span>
                  </li>
                  <li className="contact__list-item">
                    <PinIcon className="contact__list-icon" />
                    <span>
                      <span className="contact__list-label">Where we work</span>
                      <span className="contact__list-value">
                        {site.location}
                      </span>
                    </span>
                  </li>
                  <li className="contact__list-item">
                    <ClockIcon className="contact__list-icon" />
                    <span>
                      <span className="contact__list-label">Response time</span>
                      <span className="contact__list-value">
                        Replies {site.responseTime}
                      </span>
                    </span>
                  </li>
                </ul>
              </div>

              <div className="contact__panel">
                <h2 className="contact__panel-title">What happens next</h2>
                <ol className="contact__steps">
                  {whatHappensNext.map((step) => (
                    <li key={step} className="contact__step">
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </aside>
          </div>
        )}
      </div>
    </>
  );
};
